/**
 * Files API slice.
 * Leaf module (Perf 3.1.e Step 4). Pulls only the shared chokepoint from
 * ./client; never imports a sibling @/lib/api slice.
 */
import { Platform } from 'react-native';
import { fetchWithAuth, ApiError, macosUploadFile } from './client';

// File uploads can be 50MB. Align with Caddy's 300s upload header timeout
// and Node's 5-minute requestTimeout so a slow cellular 33MB send is not
// aborted by the client while the proxy is still waiting.
const FILE_UPLOAD_TIMEOUT_MS = 5 * 60 * 1000;

export const filesApi = {
  uploadFile: async ({ file, name, collectionId, conversationId, isFromTemporaryChat = false }) => {
    // macOS: FormData with file:// URI triggers RCTFileRequestHandler request-token bug.
    // Read the file as base64 via native bridge and do a raw XHR multipart upload.
    if (Platform.OS === 'macos' && file.uri) {
      const data = await macosUploadFile(file.uri, {
        fieldName: 'file',
        fileName: file.name || name || 'file',
        mimeType: file.type || 'application/octet-stream',
        endpoint: '/json/files/new',
        extraFields: {
          ...((name || file.name) ? { name: name || file.name } : {}),
          ...(collectionId ? { collectionId } : {}),
          ...(conversationId ? { conversationId } : {}),
          ...(isFromTemporaryChat ? { isFromTemporaryChat: 'true' } : {}),
        },
      });
      if (data.error) {
        throw new ApiError(data.message || 'Failed to upload file', 400, data);
      }
      return {
        file: data.file || data.existingFile,
        collectionId: collectionId,
        uploadStats: data.uploadStats
      };
    }

    const formData = new FormData();

    // Handle both web File objects and React Native file objects (with uri)
    if (file.uri) {
      // React Native file object - append with proper structure for FormData
      formData.append('file', {
        uri: file.uri,
        type: file.type || 'application/octet-stream',
        name: file.name || name || 'file',
      });
    } else {
      // Web File object
      formData.append('file', file);
    }

    if (name || file.name) {
      formData.append('name', name || file.name);
    }

    if (collectionId) {
      formData.append('collectionId', collectionId);
    }

    if (conversationId) {
      formData.append('conversationId', conversationId);
    }

    if (isFromTemporaryChat) {
      formData.append('isFromTemporaryChat', 'true');
    }

    // Uploads can be up to 50MB (e.g. audio for transcription). On slower
    // connections that easily blows past the default 30s fetch timeout and
    // aborts a request that would otherwise succeed, so give uploads the
    // same 5-minute window as the proxy and Node requestTimeout.
    const data = await fetchWithAuth('/json/files/new', {
      method: 'POST',
      body: formData,
      timeoutMs: FILE_UPLOAD_TIMEOUT_MS,
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to upload file', 400, data);
    }

    return {
      file: data.file || data.existingFile,
      collectionId: collectionId,
      uploadStats: data.uploadStats
    };
  },

  // The Files workspace owns its pagination independently of attachment pickers.
  fetchLibrary: async ({ query = '', page = 1, category = 'all', source = 'all', sort = 'newest' } = {}) => {
    const params = new URLSearchParams({ page: String(page), category, source, sort });
    if (query.trim()) params.set('q', query.trim());
    const data = await fetchWithAuth(`/json/files${query.trim() ? '/search' : ''}?${params}`);
    if (data.error) throw new ApiError(data.message || 'Unable to load files', 400, data);
    return { files: data.files || [], pagination: data.pagination };
  },

  // Fetch a single file by ID (used for polling processing status)
  fetchFile: async (fileId) => {
    const data = await fetchWithAuth(`/json/files/${fileId}`);

    if (data.error) {
      throw new ApiError(data.message || 'Failed to fetch file', 400, data);
    }

    return data.file;
  },

  // CodeViewer and the inline HTML game embed load source through this
  // authenticated path so CDN-only fetches are not the only option.
  fetchCodeText: async (fileId, { isRequestCurrent } = {}) => {
    const data = await fetchWithAuth(`/json/files/${fileId}/text`, { isRequestCurrent });
    if (data.error) {
      throw new ApiError(data.message || 'Failed to open this file', data.status || 400, data);
    }
    return data.code || data;
  },

  fetchFiles: async (page = 1, fileType = null, source = 'user') => {
    let url = `/json/files?page=${page}`;
    if (fileType) {
      url += `&fileType=${fileType}`;
    }
    if (source) {
      url += `&source=${encodeURIComponent(source)}`;
    }

    const data = await fetchWithAuth(url);

    if (data.error) {
      throw new ApiError(data.message || 'Failed to fetch files', 400, data);
    }

    return {
      files: data.files,
      pagination: data.pagination
    };
  },

  /** Same filters as GET /json/files — returns `{ total }` only (no file payload). */
  fetchFilesCount: async (fileType = null, source = 'user') => {
    const params = new URLSearchParams();
    if (fileType) params.set('fileType', fileType);
    if (source) params.set('source', source);
    const qs = params.toString();
    const data = await fetchWithAuth(`/json/files/count${qs ? `?${qs}` : ''}`);

    if (data.error) {
      throw new ApiError(data.message || 'Failed to count files', 400, data);
    }

    return { total: typeof data.total === 'number' ? data.total : parseInt(data.total, 10) || 0 };
  },

  search: async (query, page = 1, fileType = null, source = 'user') => {
    let url = `/json/files/search?q=${encodeURIComponent(query)}&page=${page}`;
    if (fileType) {
      url += `&fileType=${fileType}`;
    }
    if (source) {
      url += `&source=${encodeURIComponent(source)}`;
    }

    const data = await fetchWithAuth(url);

    if (data.error) {
      throw new ApiError(data.message || 'Failed to search files', 400, data);
    }

    return {
      files: data.files,
      pagination: data.pagination,
      query: data.query,
      fileType: data.fileType
    };
  },

  updateFile: async (fileId, name) => {
    const data = await fetchWithAuth(`/json/files/${fileId}/update`, {
      method: 'PUT',
      body: { name }
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to update file', 400, data);
    }

    return data.file;
  },

  savePresentation: async (fileId, presentationSpec) => {
    const data = await fetchWithAuth(`/json/files/${fileId}/presentation`, {
      method: 'PUT',
      body: { presentationSpec },
    });
    if (data.error) {
      throw new ApiError(data.message || 'Failed to save presentation', 400, data);
    }
    return data.file;
  },

  fetchEditableDocument: async (fileId, { isRequestCurrent } = {}) => {
    const data = await fetchWithAuth(`/json/files/${fileId}/document`, { isRequestCurrent });
    if (data.error) {
      throw new ApiError(data.message || 'Failed to fetch editable document', 400, data);
    }
    return data.document;
  },

  saveEditableDocument: async (fileId, { markdown, title, pdfOptions }) => {
    const data = await fetchWithAuth(`/json/files/${fileId}/document`, {
      method: 'PUT',
      body: { markdown, title, pdfOptions },
    });
    if (data.error) {
      throw new ApiError(data.message || 'Failed to save editable document', 400, data);
    }
    return data;
  },

  exportEditableDocumentPdf: async (fileId, { markdown, pdfOptions }) => {
    const data = await fetchWithAuth(`/json/files/${fileId}/document/export-pdf`, {
      method: 'POST',
      body: { markdown, pdfOptions },
      timeoutMs: 120_000,
    });
    if (data.error) {
      throw new ApiError(data.message || 'Failed to export PDF', 400, data);
    }
    return data.file;
  },

  exportEditableDocumentDocx: async (fileId, { markdown, pdfOptions }, { isRequestCurrent } = {}) => {
    const data = await fetchWithAuth(`/json/files/${fileId}/document/export-docx`, {
      method: 'POST',
      body: { markdown, pdfOptions },
      timeoutMs: 120_000,
      isRequestCurrent,
    });
    if (data.error) {
      throw new ApiError(data.message || 'Failed to export Word document', 400, data);
    }
    return data.file;
  },

  exportArtifactCopy: async (fileId, { kind, spec }, { isRequestCurrent } = {}) => {
    const data = await fetchWithAuth(`/json/files/${fileId}/export-copy`, {
      method: 'POST',
      body: { kind, spec },
      timeoutMs: 120_000,
      isRequestCurrent,
    });
    if (data.error) {
      throw new ApiError(data.message || 'Failed to export a copy', 400, data);
    }
    return data.file;
  },

  exportPresentation: async (fileId, presentationSpec) => {
    const data = await fetchWithAuth(`/json/files/${fileId}/presentation/export`, {
      method: 'POST',
      body: { presentationSpec },
      timeoutMs: 120_000,
    });
    if (data.error) {
      throw new ApiError(data.message || 'Failed to export PowerPoint', 400, data);
    }
    return data.file;
  },

  exportPresentationPdf: async (fileId, presentationSpec) => {
    const data = await fetchWithAuth(`/json/files/${fileId}/presentation/export-pdf`, {
      method: 'POST',
      body: { presentationSpec },
      timeoutMs: 120_000,
    });
    if (data.error) {
      throw new ApiError(data.message || 'Failed to export PDF', 400, data);
    }
    return data.file;
  },

  saveSpreadsheet: async (fileId, spreadsheetSpec) => {
    const data = await fetchWithAuth(`/json/files/${fileId}/spreadsheet`, {
      method: 'PUT',
      body: { spreadsheetSpec },
    });
    if (data.error) {
      throw new ApiError(data.message || 'Failed to save spreadsheet', 400, data);
    }
    return data.file;
  },

  exportSpreadsheet: async (fileId, spreadsheetSpec) => {
    const data = await fetchWithAuth(`/json/files/${fileId}/spreadsheet/export`, {
      method: 'POST',
      body: { spreadsheetSpec },
      timeoutMs: 120_000,
    });
    if (data.error) {
      throw new ApiError(data.message || 'Failed to export spreadsheet', 400, data);
    }
    return data.file;
  },

  exportSpreadsheetCsv: async (fileId, { spreadsheetSpec, sheetIndex = 0 } = {}) => {
    const data = await fetchWithAuth(`/json/files/${fileId}/spreadsheet/export-csv`, {
      method: 'POST',
      body: { spreadsheetSpec, sheetIndex },
      timeoutMs: 60_000,
    });
    if (data.error) {
      throw new ApiError(data.message || 'Failed to export CSV', 400, data);
    }
    return data;
  },

  // Version history for editable artifacts (AI revisions snapshot the
  // prior state server-side).
  listFileVersions: async (fileId) => {
    const data = await fetchWithAuth(`/json/files/${fileId}/versions`);
    if (data.error) {
      throw new ApiError(data.message || 'Failed to load version history', 400, data);
    }
    return data.versions || [];
  },

  restoreFileVersion: async (fileId, index) => {
    const data = await fetchWithAuth(`/json/files/${fileId}/versions/${index}/restore`, {
      method: 'POST',
    });
    if (data.error) {
      throw new ApiError(data.message || 'Failed to restore version', 400, data);
    }
    return data.file;
  },

  // Create a blank editable artifact plus a new guided chat, and return
  // { editor, file, conversation } so the client can navigate and open the
  // matching editor immediately.
  startEditorSession: async (type) => {
    // fetchWithAuth stringifies plain-object bodies itself.
    const data = await fetchWithAuth('/json/editors/start', {
      method: 'POST',
      body: { type },
    });
    if (data.error) {
      throw new ApiError(data.message || 'Failed to start the editor', 400, data);
    }
    return data;
  },

  // Create a blank (or content-seeded) editable artifact without starting a
  // new chat. Used when adding a chat reply into a document/spreadsheet while
  // no editor is open beside the conversation.
  // body: { type, markdown?, rows?, title?, conversationId?, messageId? }
  createEditorFile: async (body) => {
    const data = await fetchWithAuth('/json/editors/create', {
      method: 'POST',
      body,
    });
    if (data.error) {
      throw new ApiError(data.message || 'Failed to create the editor file', 400, data);
    }
    return { editor: data.editor, file: data.file };
  },

  // Convert an uploaded .docx/.xlsx/.csv/.pptx into an editable copy and
  // return { editor: 'document'|'spreadsheet'|'presentation', file }.
  openFileInEditor: async (fileId, { isRequestCurrent } = {}) => {
    const data = await fetchWithAuth(`/json/files/${fileId}/open-in-editor`, {
      method: 'POST',
      timeoutMs: 120_000,
      isRequestCurrent,
    });
    if (data.error) {
      throw new ApiError(data.message || 'Failed to open file in editor', 400, data);
    }
    return { editor: data.editor, file: data.file };
  },

  // Rewrite text highlighted inside the document/presentation editors.
  // mode: improve | shorten | expand | fix-grammar | simplify | professional | casual | custom
  rewriteSelection: async ({ text, mode = 'improve', instruction = '', surface = 'document', contextText = '' }) => {
    const data = await fetchWithAuth('/json/ai/rewrite-selection', {
      method: 'POST',
      body: { text, mode, instruction, surface, contextText },
      timeoutMs: 60_000,
    });
    if (data.error) {
      throw new ApiError(data.message || 'Failed to rewrite selection', 400, data);
    }
    return data.rewritten;
  },

  deleteFile: async (fileId) => {
    const data = await fetchWithAuth(`/json/files/${fileId}/delete`, {
      method: 'DELETE'
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to delete file', 400, data);
    }

    return data;
  },

  getAllowedFileTypes: async () => {
    try {
      const data = await fetchWithAuth('/json/files/allowed');

      if (data.error) {
        throw new ApiError(data.message || 'Failed to get allowed file types', 400, data);
      }

      return data.allowedTypes;
    } catch (error) {
      console.error('Error getting allowed file types:', error);
      throw error;
    }
  }
};
